// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/VeiculoEstoqueDetalhe.tsx
//  Veículo da revenda + fluxo de COMPRA no RENAVE (Fatia 2):
//    aptidão → entrada → assinatura do ATPV → NF-e de entrada → confirmação
//  Recebido por transferência (Fatia 4): a "entrada" é o pedido de transferência,
//  sem aptidão nem assinatura. Aba Transferência: autorizar outra revenda / NF-e de saída.
//  Escritas: 200 concluída · 202 pendente de conferência · 422 recusada.
//  Pendente de conferência trava o veículo até "Conferir no RENAVE".
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, ArrowLeft, Ban, CheckCircle2, Circle, Download, FileSearch, FileSignature, FileText,
  Loader2, RefreshCw, Save, SearchCheck, ShieldCheck, Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  renaveApi, baixarPdf, cpfOperadorSalvo, cpfValido, descricaoVeiculo, formatarCnpj, formatarMoeda, lerArquivoBase64,
  salvarCpfOperador, ORIGEM, ORIGEM_LABEL, SITUACAO, STATUS_OPERACAO, STATUS_OPERACAO_LABEL, TIPO_OPERACAO_LABEL, TIPOS_ASSINATURA, tiposAssinaturaPara,
  type Aptidao, type ConferenciaFiscal, type ResultadoOperacao,
} from '@/features/renave/api';
import { VeiculoEstoqueForm, estadoDe, paraInput, type VeiculoFormState } from '@/features/renave/VeiculoEstoqueForm';
import { VendaRenave } from '@/features/renave/VendaRenave';
import { TransferenciaRenave } from '@/features/renave/TransferenciaRenave';
import { ConferenciaFiscalPainel } from '@/features/renave/ConferenciaFiscalPainel';
import { SituacaoBadge } from './VeiculosEstoque';

export default function VeiculoEstoqueDetalhe() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const veiculoQ = useQuery({ queryKey: ['veiculo-estoque', id], queryFn: () => renaveApi.obter(id), enabled: id > 0 });
  const opsQ = useQuery({ queryKey: ['veiculo-estoque-ops', id], queryFn: () => renaveApi.operacoes(id), enabled: id > 0 });
  const configQ = useQuery({ queryKey: ['renave-config'], queryFn: renaveApi.config });

  const v = veiculoQ.data;
  const ops = useMemo(() => opsQ.data ?? [], [opsQ.data]);

  const [cpf, setCpf] = useState(() => cpfOperadorSalvo(user?.id));
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [aptidao, setAptidao] = useState<Aptidao | null>(null);
  const [form, setForm] = useState<VeiculoFormState | null>(null);
  const [chaveNfe, setChaveNfe] = useState('');
  const [tipoAssinatura, setTipoAssinatura] = useState<string>(TIPOS_ASSINATURA[0].valor);
  const tiposValidos = tiposAssinaturaPara(v?.tipoCrv ?? null);
  // Mantém o tipo coerente com o CRV (papel-moeda × digital).
  useEffect(() => {
    if (!tiposValidos.some(t => t.valor === tipoAssinatura)) setTipoAssinatura(tiposValidos[0].valor);
  }, [v?.tipoCrv]); // eslint-disable-line react-hooks/exhaustive-deps
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [p7s, setP7s] = useState<File | null>(null);
  const [confirmarCancelamento, setConfirmarCancelamento] = useState(false);
  const [confEntrada, setConfEntrada] = useState<ConferenciaFiscal | null>(null);

  useEffect(() => { if (v) setForm(estadoDe(v)); }, [v]);
  useEffect(() => { if (!cpf) setCpf(cpfOperadorSalvo(user?.id)); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendente = ops.find(o => o.status === STATUS_OPERACAO.Incerta || o.status === STATUS_OPERACAO.EmAndamento);
  const assinaturaOk = ops.some(o => o.tipo === 3 && o.status === STATUS_OPERACAO.Concluida);
  const emCompra = v?.situacao === SITUACAO.EmCompra;
  const emEstoque = v?.situacao === SITUACAO.EmEstoque;
  const vendido = v?.situacao === SITUACAO.Vendido;
  const transferido = v?.situacao === SITUACAO.Transferido;
  const recebido = !!v && v.origemEntrada !== ORIGEM.Compra;
  const deFilial = v?.origemEntrada === ORIGEM.TransferenciaFilial;
  const ativo = !!configQ.data?.ativo;
  const cpfOk = cpfValido(cpf);

  const recarregar = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['veiculo-estoque', id] }),
      qc.invalidateQueries({ queryKey: ['veiculo-estoque-ops', id] }),
      qc.invalidateQueries({ queryKey: ['veiculos-estoque'] }),
    ]);
  };

  const exigirCpf = () => {
    if (!cpfOk) { toast.error('Informe um CPF válido do operador responsável (exigido pelo RENAVE).'); return null; }
    salvarCpfOperador(user?.id, cpf);
    return cpf.replace(/\D/g, '');
  };

  /** Executa uma escrita no RENAVE e traduz o resultado para o operador. */
  const escrever = async (rotulo: string, acao: () => Promise<ResultadoOperacao>) => {
    setOcupado(rotulo);
    try {
      const r = await acao();
      if (r.status === STATUS_OPERACAO.Concluida || r.status === STATUS_OPERACAO.Descartada) toast.success(r.mensagem);
      else if (r.status === STATUS_OPERACAO.Incerta) toast.warning(r.mensagem, { duration: 12_000 });
      else toast.error(r.mensagem + (r.logIdRastreabilidade ? ` (protocolo ${r.logIdRastreabilidade})` : ''), { duration: 12_000 });
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setOcupado(null);
      await recarregar();
    }
  };

  const ler = async <T,>(rotulo: string, acao: () => Promise<T>, aoTerminar: (t: T) => void) => {
    setOcupado(rotulo);
    try { aoTerminar(await acao()); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setOcupado(null); }
  };

  const salvarDados = async () => {
    if (!form) return;
    setOcupado('dados');
    try {
      await renaveApi.atualizar(id, paraInput(form));
      toast.success('Dados do veículo salvos.');
      await recarregar();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setOcupado(null);
    }
  };

  const enviarAssinatura = async () => {
    const op = exigirCpf(); if (!op) return;
    const tipo = TIPOS_ASSINATURA.find(t => t.valor === tipoAssinatura)!;
    if (tipo.arquivo && !arquivo) { toast.error('Escolha a foto/arquivo do documento assinado.'); return; }
    if (tipo.p7s && !p7s) { toast.error('Escolha o arquivo .p7s da assinatura digital.'); return; }
    const arquivoBase64 = arquivo && tipo.arquivo ? await lerArquivoBase64(arquivo) : null;
    const assinaturaP7sBase64 = p7s && tipo.p7s ? await lerArquivoBase64(p7s) : null;
    await escrever('assinatura', () => renaveApi.assinaturaAtpv(id, { tipo: tipo.valor, arquivoBase64, assinaturaP7sBase64, cpfOperador: op }));
    setArquivo(null); setP7s(null);
  };

  if (veiculoQ.isLoading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando…</div>;
  }
  if (!v || !form) {
    return <div className="p-6 text-destructive">{getApiErrorMessage(veiculoQ.error, 'Veículo não encontrado.')}</div>;
  }

  const ocupadoCom = (r: string) => ocupado === r;
  const travado = !!ocupado || !!pendente;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* ── Cabeçalho ───────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/estoque/veiculos')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{descricaoVeiculo(v)}</h1>
            <p className="font-mono text-sm text-muted-foreground">{v.placa ?? 'sem placa'} · {v.chassi}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {configQ.data?.ambiente === 2 && <Badge variant="outline">Homologação</Badge>}
          <SituacaoBadge situacao={v.situacao} />
        </div>
      </div>

      {!ativo && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>RENAVE desativado</AlertTitle>
          <AlertDescription>
            Ative em <button className="underline" onClick={() => navigate('/configuracoes/renave')}>Configurações → RENAVE</button> para registrar este veículo.
          </AlertDescription>
        </Alert>
      )}

      {v.divergenciaRenave && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Divergência com o RENAVE</AlertTitle>
          <AlertDescription>
            {v.divergenciaRenave} Confira no RENAVE/Detran e corrija; "Atualizar situação" relê o registro.
          </AlertDescription>
        </Alert>
      )}

      {pendente && (
        <Alert className="border-amber-500/50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle>
            {pendente.status === STATUS_OPERACAO.Incerta ? 'Operação pendente de conferência' : 'Operação em andamento'}
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {TIPO_OPERACAO_LABEL[pendente.tipo]}: o RENAVE não confirmou o resultado
              {pendente.mensagemUsuario ? ` (${pendente.mensagemUsuario})` : ''}. Ela pode ter sido efetivada.
              Confira antes de qualquer outra operação. Reenviar às cegas pode duplicar o registro.
            </p>
            {pendente.status === STATUS_OPERACAO.Incerta && (
              <Button size="sm" onClick={() => escrever('conferir', () => renaveApi.conferir(id))} disabled={!!ocupado}>
                {ocupadoCom('conferir') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <SearchCheck className="mr-2 h-4 w-4" />}
                Conferir no RENAVE
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="space-y-1.5">
            <Label htmlFor="cpf-operador" className="text-xs">CPF do operador responsável</Label>
            <Input id="cpf-operador" value={cpf} onChange={e => setCpf(e.target.value)} inputMode="numeric"
              maxLength={14} className={`w-48 ${cpf && !cpfOk ? 'border-destructive' : ''}`} placeholder="000.000.000-00" />
          </div>
          <p className="max-w-md pb-2 text-xs text-muted-foreground">
            O RENAVE registra quem fez cada operação. Fica lembrado neste navegador.
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue={vendido ? 'venda' : transferido ? 'transferencia' : 'fluxo'}>
        <TabsList>
          <TabsTrigger value="fluxo">{recebido ? 'Entrada por transferência' : 'Compra no RENAVE'}</TabsTrigger>
          {(emEstoque || vendido) && <TabsTrigger value="venda">Venda no RENAVE</TabsTrigger>}
          {(emEstoque || transferido) && <TabsTrigger value="transferencia">Transferência</TabsTrigger>}
          <TabsTrigger value="dados">Dados do veículo</TabsTrigger>
          <TabsTrigger value="historico">Histórico ({ops.length})</TabsTrigger>
        </TabsList>

        {/* ── Fluxo de compra ──────────────────────────────────────────── */}
        <TabsContent value="fluxo" className="space-y-4">

          {recebido && (
            <p className="text-sm text-muted-foreground">
              {ORIGEM_LABEL[v.origemEntrada]} de <strong>{formatarCnpj(v.cnpjOrigemTransferencia)}</strong>.
              {deFilial
                ? ' Entre filiais o estoque já nasce confirmado e não há CRV novo.'
                : ' A revenda de origem precisa ter autorizado este CNPJ; o Detran confirma depois.'}
              {' '}O ATPVe gerado pelo RENAVE dispensa a assinatura do vendedor.
            </p>
          )}

          {!recebido && <Passo n={1} titulo="Aptidão do veículo" feito={!!aptidao?.diagnostico?.veiculoApto}
            descricao="Prévia: confere restrições, débitos e se o RENAVE aceitará a entrada. Recomendado logo antes da entrada.">
            <Button variant="outline" size="sm" disabled={!ativo || !!ocupado}
              onClick={() => ler('aptidao', () => renaveApi.aptidao(id), setAptidao)}>
              {ocupadoCom('aptidao') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Consultar aptidão
            </Button>
            {aptidao && <ResultadoAptidao a={aptidao} />}
          </Passo>}

          <Passo n={recebido ? 1 : 2}
            titulo={recebido ? 'Pedido de transferência' : v.veiculoProprio ? 'Entrada de veículo próprio' : 'Entrada em estoque'}
            feito={!emCompra && v.renaveIdEstoque != null}
            descricao={recebido
              ? 'Pede ao RENAVE a transferência do estoque de origem para esta empresa.'
              : 'Registra a compra: a revenda passa a ser a responsável legal pelo veículo.'}>
            {v.renaveIdEstoque != null ? (
              <p className="text-sm">Estoque RENAVE nº <strong>{v.renaveIdEstoque}</strong> · {v.renaveEstado ?? '—'}</p>
            ) : (
              <Button size="sm" disabled={!ativo || !emCompra || travado}
                onClick={() => { const op = exigirCpf(); if (op) void escrever('entrada', () => renaveApi.entrada(id, op)); }}>
                {ocupadoCom('entrada') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {recebido ? 'Solicitar transferência no RENAVE' : 'Registrar entrada no RENAVE'}
              </Button>
            )}
          </Passo>

          {!v.veiculoProprio && !recebido && (
            <Passo n={3} titulo="Assinatura do ATPV pelo vendedor" feito={assinaturaOk}
              descricao="Se o vendedor assinar pelo app Carteira Digital de Trânsito (gov.br prata/ouro), não é preciso enviar nada aqui.">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" disabled={!emEstoque || !!ocupado}
                  onClick={() => ler('atpv', () => renaveApi.atpv(id), baixarPdf)}>
                  {ocupadoCom('atpv') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Baixar ATPVe
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Tipo de assinatura (CRV {v.tipoCrv === 'DIGITAL' ? 'digital' : 'em papel'})</Label>
                  <Select value={tipoAssinatura} onValueChange={setTipoAssinatura}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {tiposValidos.map(t => <SelectItem key={t.valor} value={t.valor}>{t.rotulo}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {TIPOS_ASSINATURA.find(t => t.valor === tipoAssinatura)?.arquivo && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Foto/arquivo do documento</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={e => setArquivo(e.target.files?.[0] ?? null)} />
                  </div>
                )}
                {TIPOS_ASSINATURA.find(t => t.valor === tipoAssinatura)?.p7s && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Assinatura digital (.p7s)</Label>
                    <Input type="file" accept=".p7s" onChange={e => setP7s(e.target.files?.[0] ?? null)} />
                  </div>
                )}
              </div>
              <Button size="sm" disabled={!emEstoque || travado} onClick={() => void enviarAssinatura()}>
                {ocupadoCom('assinatura') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSignature className="mr-2 h-4 w-4" />}
                {assinaturaOk ? 'Reenviar assinatura' : 'Enviar assinatura'}
              </Button>
            </Passo>
          )}

          <Passo n={recebido ? 2 : v.veiculoProprio ? 3 : 4} titulo="NF-e de entrada" feito={!!v.chaveNfEntrada}
            descricao="Chave da NF-e da compra (emitida pela revenda contra o vendedor PF, ou pelo vendedor PJ). Precisa estar autorizada na SEFAZ.">
            {v.chaveNfEntrada && <p className="break-all font-mono text-xs">{v.chaveNfEntrada}</p>}
            <div className="flex flex-wrap gap-2">
              <Input value={chaveNfe} onChange={e => { setChaveNfe(e.target.value.replace(/\D/g, '')); setConfEntrada(null); }} maxLength={44}
                inputMode="numeric" placeholder="Chave de 44 dígitos" className="w-[26rem] max-w-full font-mono text-xs" />
              <Button variant="outline" size="sm" disabled={!!ocupado}
                onClick={() => ler('conf-entrada', () => renaveApi.conferenciaFiscal(id, 'COMPRA', chaveNfe || undefined),
                  c => { setConfEntrada(c); if (c.nota) setChaveNfe(c.nota.chave); })}>
                {ocupadoCom('conf-entrada') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSearch className="mr-2 h-4 w-4" />}
                {chaveNfe ? 'Conferir NF-e' : 'Buscar NF-e no Gestor'}
              </Button>
              <Button size="sm" disabled={!emEstoque || travado || chaveNfe.length !== 44 || (confEntrada?.bloqueios.length ?? 0) > 0}
                onClick={() => { const op = exigirCpf(); if (op) void escrever('nf', () => renaveApi.notaFiscalEntrada(id, chaveNfe, op)).then(() => setChaveNfe('')); }}>
                {ocupadoCom('nf') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                {v.chaveNfEntrada ? 'Trocar chave' : 'Enviar NF-e'}
              </Button>
            </div>
            {confEntrada && <ConferenciaFiscalPainel c={confEntrada} />}
          </Passo>

          <Passo n={recebido ? 3 : v.veiculoProprio ? 4 : 5} titulo="Confirmação do Detran" feito={v.renaveEstado === 'CONFIRMADO'}
            descricao="O Detran confirma a entrada e emite o CRV novo. O código de segurança desse CRV será pedido na venda.">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">Situação no RENAVE: <strong>{v.renaveEstado ?? '—'}</strong></span>
              {v.renaveSincronizadoEm && (
                <span className="text-xs text-muted-foreground">(lida em {new Date(v.renaveSincronizadoEm).toLocaleString('pt-BR')})</span>
              )}
              <Button variant="outline" size="sm" disabled={v.renaveIdEstoque == null || !!ocupado}
                onClick={() => ler('sinc', () => renaveApi.sincronizar(id), async () => { toast.success('Situação atualizada.'); await recarregar(); })}>
                {ocupadoCom('sinc') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Atualizar situação
              </Button>
              <Button variant="outline" size="sm" disabled={v.renaveIdEstoque == null || !!ocupado}
                onClick={() => ler('termo', () => renaveApi.termoEntrada(id), baixarPdf)}>
                {ocupadoCom('termo') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Termo de entrada
              </Button>
            </div>
          </Passo>

          {emEstoque && !recebido && (
            <Card className="border-destructive/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Desfazer a compra</CardTitle>
                <CardDescription>Cancela a entrada no RENAVE (desistência). Depois que o Detran confirma, o RENAVE não aceita mais o cancelamento.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="destructive" size="sm" disabled={travado} onClick={() => setConfirmarCancelamento(true)}>
                  {ocupadoCom('cancelar') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ban className="mr-2 h-4 w-4" />}
                  Cancelar entrada
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Venda (Fatia 3) ──────────────────────────────────────────── */}
        <TabsContent value="venda">
          <VendaRenave v={v} ocupado={ocupado} travado={travado} exigirCpf={exigirCpf} escrever={escrever} ler={ler} />
        </TabsContent>

        {/* ── Transferência (Fatia 4) ───────────────────────────────────── */}
        <TabsContent value="transferencia">
          <TransferenciaRenave v={v} ocupado={ocupado} travado={travado} exigirCpf={exigirCpf} escrever={escrever} ler={ler} />
        </TabsContent>

        {/* ── Dados ─────────────────────────────────────────────────────── */}
        <TabsContent value="dados">
          <Card>
            <CardContent className="space-y-4 pt-6">
              {!emCompra && (
                <p className="text-xs text-muted-foreground">
                  Identificação e compra ficam travadas depois da entrada no RENAVE. O CRV pode ser atualizado até a venda.
                </p>
              )}
              <VeiculoEstoqueForm valor={form} onChange={setForm} identificacaoTravada={!emCompra}
                possuiCodigoSegurancaCrv={v.possuiCodigoSegurancaCrv} />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  <button type="button" className="underline hover:text-foreground" onClick={() => navigate(`/produtos/${v.produtoId}`)}>Ver produto no estoque</button>
                  {' '}· compra {formatarMoeda(v.valorCompra)}
                </span>
                <Button onClick={salvarDados} disabled={!!ocupado || v.situacao === SITUACAO.Cancelado || v.situacao === SITUACAO.Vendido || transferido}>
                  {ocupadoCom('dados') ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Histórico ─────────────────────────────────────────────────── */}
        <TabsContent value="historico">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Operação</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead>Mensagem</TableHead>
                  <TableHead>Protocolo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!ops.length ? (
                  <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">Nenhuma operação enviada ao RENAVE.</TableCell></TableRow>
                ) : ops.map(o => (
                  <TableRow key={o.id}>
                    <TableCell className="whitespace-nowrap text-xs">{new Date(o.dtInicio).toLocaleString('pt-BR')}</TableCell>
                    <TableCell className="text-sm">{TIPO_OPERACAO_LABEL[o.tipo] ?? o.tipo}{o.tentativas > 1 ? ` (${o.tentativas}×)` : ''}</TableCell>
                    <TableCell>
                      <Badge variant={o.status === STATUS_OPERACAO.Concluida ? 'default' : o.status === STATUS_OPERACAO.Rejeitada ? 'destructive' : 'outline'}>
                        {STATUS_OPERACAO_LABEL[o.status] ?? o.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs text-xs">{o.mensagemUsuario ?? o.resolucao ?? ''}</TableCell>
                    <TableCell className="font-mono text-xs">{o.logIdRastreabilidade ?? (o.renaveIdEstoque ? `estoque ${o.renaveIdEstoque}` : '')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmarCancelamento} onOpenChange={setConfirmarCancelamento}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar a entrada no RENAVE?</AlertDialogTitle>
            <AlertDialogDescription>
              A compra é desfeita no RENAVE e a responsabilidade volta ao vendedor. O veículo e o produto dele ficam inativos no Gestor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { const op = exigirCpf(); if (op) void escrever('cancelar', () => renaveApi.cancelarEntrada(id, op)); }}>
              Cancelar entrada
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Passo({ n, titulo, descricao, feito, children }: {
  n: number; titulo: string; descricao: string; feito: boolean; children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          {feito
            ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
            : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />}
          <div>
            <CardTitle className="text-base">{n}. {titulo}</CardTitle>
            <CardDescription>{descricao}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pl-14">{children}</CardContent>
    </Card>
  );
}

function ResultadoAptidao({ a }: { a: Aptidao }) {
  const apto = a.diagnostico?.veiculoApto;
  const d = a.informacoesDebitos;
  const debitos: [string, number | null | undefined][] = [
    ['IPVA', d?.valorDebitoIpva], ['Licenciamento', d?.valorDebitoLicenciamento], ['Multas', d?.valorDebitoMultas],
    ['DPVAT', d?.valorDebitoDpvat], ['Taxas e diversos', d?.valorTaxasEDebitosDiversos],
  ];
  return (
    <div className="space-y-2 rounded-md border p-3 text-sm">
      <p className="font-medium">
        {apto === true && <span className="text-green-600">Apto para entrar em estoque.</span>}
        {apto === false && <span className="text-destructive">Não apto.</span>}
        {apto == null && <span className="text-amber-600">Não foi possível determinar a aptidão.</span>}
        {a.veiculo?.ufJurisdicao && <span className="ml-2 text-xs text-muted-foreground">UF {a.veiculo.ufJurisdicao}</span>}
      </p>
      {a.comunicacaoComDetranFalhou && <p className="text-xs text-amber-600">O Detran não respondeu; tente de novo mais tarde.</p>}
      {!!a.diagnostico?.motivosParaNaoAptidao?.length && (
        <ul className="list-disc pl-5 text-xs">{a.diagnostico.motivosParaNaoAptidao.map(m => <li key={m}>{m}</li>)}</ul>
      )}
      {d?.existemDebitos && (
        <div className="space-y-1 text-xs">
          <p className="font-medium">Débitos do veículo</p>
          {debitos.filter(([, val]) => (val ?? 0) > 0).map(([n, val]) => <p key={n}>{n}: {formatarMoeda(val)}</p>)}
          {d.boletos?.map(b => (
            <p key={b.numeroCodigoBarras ?? ''} className="font-mono">
              {formatarMoeda(b.valorBoleto)} · venc. {b.dataVencimentoBoleto ?? '—'} · {b.numeroCodigoBarras}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
